import { FormFeedBack, ModalBase, Select } from '@repo/ui';
import { ListCourseResponse } from 'domain/models/responses';
import { GetAllEntitiesResponse } from 'domain/models/responses';
import { PartnersServiceInterface } from 'domain/services';
import { FormikValues } from 'formik';
import { useEffect, useState } from 'react';
import { Col, Form, Input, Label, Row, Spinner } from 'reactstrap';

import { Partner } from '../../models';
import { ModalCreatePartnerBase } from '../modal-create-partner-base';

const DEBOUNCE_MS = 500;

const AVAILABLE_FIELDS = [
  { key: 'nome', title: 'Nome Completo' },
  { key: 'email', title: 'E-mail' },
  { key: 'avatar', title: 'Foto de Perfil' },
  { key: 'curso', title: 'Curso' },
  { key: 'entidade', title: 'Instituição' },
  { key: 'data_nascimento', title: 'Data de Nascimento' },
  { key: 'unidades_curriculares', title: 'Unidades Curriculares' },
];

interface IProps {
  isOpen: boolean;
  loading: boolean;
  formik: FormikValues;
  partnersList: Partner[];
  entitiesList: GetAllEntitiesResponse[];
  coursesList: ListCourseResponse[];
  partnersService: PartnersServiceInterface;
  toggleModal: () => void;
  resetForm: () => void;
  onPartnerCreated: () => void;
  onKeyGenerated: (key: string, partnerName: string) => void;
}

export const ModalCreatePartner = ({
  isOpen,
  loading,
  formik,
  partnersList,
  entitiesList,
  coursesList,
  partnersService,
  toggleModal,
  resetForm,
  onPartnerCreated,
  onKeyGenerated,
}: IProps) => {
  const entitiesOptions = entitiesList.map((e) => ({ label: e.name, value: e.id }));
  const coursesOptions = coursesList.map((c) => ({ label: c.nome, value: c.id }));
  const [previewKey, setPreviewKey] = useState('');
  const [loadingKey, setLoadingKey] = useState(false);
  const [isModalPartnerBaseOpen, setIsModalPartnerBaseOpen] = useState(false);

  const partnersOptions = partnersList.map((p) => ({
    label: p.name,
    value: p.id,
  }));

  useEffect(() => {
    const name = formik.values.partnerName?.trim();

    if (!name) {
      setPreviewKey('');
      return;
    }

    setLoadingKey(true);

    const timer = setTimeout(async () => {
      try {
        const res = await partnersService.getKeyPreview({ name });
        setPreviewKey(res.key);
      } catch {
        setPreviewKey('');
      } finally {
        setLoadingKey(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      setLoadingKey(false);
    };
  }, [formik.values.partnerName]);

  useEffect(() => {
    if (!isOpen) setPreviewKey('');
  }, [isOpen]);

  const handlePartnerCreated = (partner: { id: number; name: string; logoKey?: string }) => {
    formik.setFieldValue('partner', { label: partner.name, value: partner.id });
    formik.setFieldValue('partnerName', partner.name);
    onPartnerCreated();
  };

  return (
    <>
      <ModalCreatePartnerBase
        isOpen={isModalPartnerBaseOpen}
        partnersService={partnersService}
        toggleModal={() => setIsModalPartnerBaseOpen(false)}
        onCreated={handlePartnerCreated}
        onKeyGenerated={onKeyGenerated}
      />

      <ModalBase.Wrapper
        size="lg"
        isOpen={isOpen}
        loading={loading}
        toggle={toggleModal}
        onClosed={resetForm}
      >
        <ModalBase.Header toggle={toggleModal} withBorderBottom>
          Nova Solicitação de Parceiro
        </ModalBase.Header>

        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <ModalBase.Body>
            <Row>
              {/* ── Parceiro ─────────────────────────────── */}
              <Col md={12} className="mb-1">
                <p className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Parceiro
                </p>
              </Col>

              <Col md={9} className="mb-3">
                <Label htmlFor="partner" className="form-label">
                  Selecione o parceiro
                </Label>
                <Select
                  id="partner"
                  name="partner"
                  placeholder="Selecione..."
                  isClearable
                  options={partnersOptions}
                  value={formik.values.partner}
                  onChange={(option: any) => {
                    formik.setFieldValue('partner', option);
                    formik.setFieldValue('partnerName', option?.label ?? '');
                  }}
                  onBlur={formik.handleBlur}
                />
              </Col>

              <Col md={3} className="mb-3 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-outline-primary w-100"
                  onClick={() => setIsModalPartnerBaseOpen(true)}
                >
                  <i className="ri-add-line me-1" />
                  Novo parceiro
                </button>
              </Col>

              {/* ── Solicitação ──────────────────────────── */}
              <Col md={12} className="mb-1 mt-2">
                <p className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Solicitação
                </p>
              </Col>

              <Col md={8} className="mb-3">
                <Label htmlFor="partnerName" className="form-label">
                  Nome da solicitação
                </Label>
                <Input
                  id="partnerName"
                  type="text"
                  name="partnerName"
                  autoFocus
                  className="form-control"
                  placeholder="Ex: PREMIA - Dados de estágio"
                  value={formik.values.partnerName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.partnerName && !!formik.errors.partnerName
                  }
                />
                <FormFeedBack
                  error={formik.errors.partnerName}
                  isInvalid={
                    formik.touched.partnerName && !!formik.errors.partnerName
                  }
                />
              </Col>

              <Col md={4} className="mb-3">
                <Label className="form-label">Chave gerada</Label>
                <div
                  className="form-control d-flex align-items-center gap-2"
                  style={{ background: '#f8f9fa', minHeight: 38 }}
                >
                  {loadingKey ? (
                    <Spinner size="sm" color="secondary" />
                  ) : (
                    <code className="text-muted" style={{ fontSize: 13 }}>
                      {previewKey || <span className="fst-italic">—</span>}
                    </code>
                  )}
                </div>
                <small className="text-muted">Gerada automaticamente pelo nome</small>
              </Col>

              <Col md={12} className="mb-3">
                <Label htmlFor="createDescription" className="form-label">
                  Descrição
                </Label>
                <Input
                  id="createDescription"
                  type="textarea"
                  name="description"
                  rows={2}
                  className="form-control"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.description && !!formik.errors.description
                  }
                />
                <FormFeedBack
                  error={formik.errors.description}
                  isInvalid={
                    formik.touched.description && !!formik.errors.description
                  }
                />
              </Col>

              <Col md={12} className="mb-3">
                <Label htmlFor="createRedirectUrl" className="form-label">
                  URL de redirecionamento
                </Label>
                <Input
                  id="createRedirectUrl"
                  type="url"
                  name="redirectUrl"
                  className="form-control"
                  placeholder="https://..."
                  value={formik.values.redirectUrl}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.redirectUrl && !!formik.errors.redirectUrl
                  }
                />
                <FormFeedBack
                  error={formik.errors.redirectUrl}
                  isInvalid={
                    formik.touched.redirectUrl && !!formik.errors.redirectUrl
                  }
                />
              </Col>

              <Col md={12}>
                <div className="form-check form-switch">
                  <Input
                    id="createActive"
                    type="checkbox"
                    name="active"
                    role="switch"
                    className="form-check-input"
                    checked={formik.values.active}
                    onChange={formik.handleChange}
                  />
                  <Label className="form-check-label" htmlFor="createActive">
                    {formik.values.active ? 'Ativo' : 'Inativo'}
                  </Label>
                </div>
              </Col>

              {/* ── Escopo ──────────────────────────────── */}
              <Col md={12} className="mb-1 mt-3">
                <p className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Escopo
                </p>
                <small className="text-muted d-block mb-3">
                  Deixe em branco para aplicar a todas as organizações/cursos.
                </small>
              </Col>

              <Col md={6} className="mb-3">
                <Label className="form-label">Organizações</Label>
                <Select
                  isMulti
                  isClearable
                  placeholder="Todas as organizações"
                  options={entitiesOptions}
                  value={formik.values.organizations}
                  onChange={(opts: any) => formik.setFieldValue('organizations', opts ?? [])}
                />
              </Col>

              <Col md={6} className="mb-3">
                <Label className="form-label">Cursos</Label>
                <Select
                  isMulti
                  isClearable
                  placeholder="Todos os cursos"
                  options={coursesOptions}
                  value={formik.values.courses}
                  onChange={(opts: any) => formik.setFieldValue('courses', opts ?? [])}
                />
              </Col>

              {/* ── Dados solicitados ────────────────────── */}
              <Col md={12} className="mb-1 mt-3">
                <p className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Dados solicitados
                </p>
              </Col>

              <Col md={12}>
                <div className="border rounded">
                  <div className="d-flex px-3 py-2 border-bottom bg-light rounded-top">
                    <span className="flex-grow-1 text-muted fw-semibold" style={{ fontSize: 12 }}>Campo</span>
                    <span className="text-muted fw-semibold me-3" style={{ fontSize: 12, width: 60, textAlign: 'center' }}>Incluir</span>
                    <span className="text-muted fw-semibold" style={{ fontSize: 12, width: 90, textAlign: 'center' }}>Obrigatório</span>
                  </div>

                  {AVAILABLE_FIELDS.map((available, i) => {
                    type Field = { key: string; title: string; mandatory: boolean };
                    const selected: Field | undefined = formik.values.requestedData.find(
                      (f: Field) => f.key === available.key
                    );
                    const isChecked = !!selected;

                    const toggle = () => {
                      if (isChecked) {
                        formik.setFieldValue(
                          'requestedData',
                          formik.values.requestedData.filter((f: Field) => f.key !== available.key)
                        );
                      } else {
                        formik.setFieldValue('requestedData', [
                          ...formik.values.requestedData,
                          { key: available.key, title: available.title, mandatory: false },
                        ]);
                      }
                    };

                    return (
                      <div
                        key={available.key}
                        className={`d-flex align-items-center px-3 py-2${i < AVAILABLE_FIELDS.length - 1 ? ' border-bottom' : ''}`}
                        style={{ background: isChecked ? '#f0f9ff' : 'transparent', cursor: 'pointer' }}
                        onClick={toggle}
                      >
                        <span className="flex-grow-1" style={{ fontSize: 14 }}>{available.title}</span>

                        <div style={{ width: 60, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <Input
                            type="checkbox"
                            id={`field-include-${available.key}`}
                            checked={isChecked}
                            onChange={toggle}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>

                        <div style={{ width: 90, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <Input
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            id={`field-mandatory-${available.key}`}
                            checked={selected?.mandatory ?? false}
                            disabled={!isChecked}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              formik.setFieldValue(
                                'requestedData',
                                formik.values.requestedData.map((f: Field) =>
                                  f.key === available.key ? { ...f, mandatory: e.target.checked } : f
                                )
                              );
                            }}
                            style={{ cursor: isChecked ? 'pointer' : 'not-allowed' }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Col>
            </Row>
          </ModalBase.Body>

          <ModalBase.Footer>
            <ModalBase.Actions
              loading={loading}
              goBack={toggleModal}
              actionBtnType="submit"
              confirmBtnLabel="Criar solicitação"
            />
          </ModalBase.Footer>
        </Form>
      </ModalBase.Wrapper>
    </>
  );
};
